import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Globe, Zap, FileText, Star, Clock, HelpCircle, ArrowRight, Languages, ChevronDown, ChevronUp } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const Pricing = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const PRICING_SCHEMA = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Fiyatlandırma - ÇeviriPro",
        "description": "ÇeviriPro avantajlı fiyatlandırma seçenekleri. İhtiyacınıza en uygun çeviri paketini seçin.",
        "url": "https://ceviripro.com.tr/fiyatlandirma"
    };

    const faqs = [
        {
            question: "Karakter sayımı nasıl çalışır?",
            answer: "Karakter sayacı, belgenizdeki boşluklar dahil tüm harf, sayı ve noktalama işaretlerini hesaplar. Yüklediğiniz dosyanın formatından bağımsız olarak, çeviri öncesi sistemimiz salt metni çıkarır ve bu metin üzerinden kesin karakter ölçümü yapar."
        },
        {
            question: "Kullanmadığım karakterler sonraki aya devreder mi?",
            answer: "Paketlerimiz tek seferlik (kontör mantığı) olduğu için bir süre sınırı yoktur. Satın aldığınız karakterleri hesabınızda dilediğiniz zaman kullanabilirsiniz."
        },
        {
            question: "Hangi dilleri destekliyorsunuz?",
            answer: "Sistemimiz şu anda 100'den fazla dil arasında çift yönlü çeviriyi desteklemektedir. Tüm paketlerimizde tüm dil seçenekleri aktiftir."
        }
    ];

    const toggleFaq = (index) => {
        if (openFaq === index) {
            setOpenFaq(null);
        } else {
            setOpenFaq(index);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <SEO
                title="Fiyatlandırma | ÇeviriPro"
                description="ÇeviriPro ile ihtiyaçlarınıza uygun fiyat seçeneklerini inceleyin. Hızlı, güvenilir ve uygun maliyetli belge çeviri hizmeti."
                canonical="/fiyatlandirma"
                schema={PRICING_SCHEMA}
            />

            {/* Navbar (Adapted for Blue Theme) */}
            <nav className="bg-white sticky top-0 z-50 border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo192.png" alt="CeviriPro Logo" className="w-8 h-8 object-contain drop-shadow-sm" />
                            <span className="text-xl font-bold text-slate-900">
                                ÇeviriPro
                            </span>
                        </Link>
                        <div className="hidden md:flex items-center gap-8">
                            <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                                Ana Sayfa
                            </Link>
                            <Link to="/ozellikler" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                                Özellikler
                            </Link>
                            <Link to="/fiyatlandirma" className="text-slate-900 font-bold transition-colors">
                                Fiyatlandırma
                            </Link>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link
                                to="/login"
                                className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                            >
                                Giriş Yap
                            </Link>
                            <Link
                                to="/register"
                                className="px-5 py-2 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 hover:shadow-lg transition-all"
                            >
                                Kaydol
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Pricing Header */}
            <div className="pt-20 pb-16 px-4 bg-slate-900 relative overflow-hidden">
                {/* Subtle background decoration */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-20">
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500 rounded-full blur-3xl mix-blend-screen"></div>
                    <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-indigo-500 rounded-full blur-3xl mix-blend-screen"></div>
                </div>

                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
                        İhtiyacınıza Uygun <span className="text-blue-400">Fiyatlandırma</span>
                    </h1>
                    <p className="text-xl text-slate-300 max-w-2xl mx-auto font-light">
                        Esnek paketlerle sadece ihtiyacınız olan kadar ödeyin. Gizli ücret veya sürpriz ödemeler yok.
                    </p>
                </div>
            </div>

            {/* Pricing Cards */}
            <div className="flex-grow pb-24 px-4 -mt-8 relative z-20">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-8 lg:gap-12 items-start">

                        {/* Starter Tier */}
                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 flex flex-col h-full transition-transform hover:-translate-y-2 duration-300">
                            <div className="mb-6">
                                <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
                                    Başlangıç
                                </span>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-extrabold text-slate-900">$1.99</span>
                                </div>
                                <p className="text-slate-500 mt-2 text-sm">Temel çeviri ihtiyaçlarınız için.</p>
                            </div>

                            <div className="flex-grow">
                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700 font-medium">100.000 Karakter</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">Tüm diller destekleniyor</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">Standart işlem hızı</span>
                                    </li>
                                </ul>
                            </div>

                            <a
                                href="#" // Placeholder for Shopify Link
                                className="w-full block text-center py-4 bg-slate-50 hover:bg-slate-100 text-slate-900 border border-slate-200 rounded-xl font-bold transition-colors"
                            >
                                Hemen Başla
                            </a>
                        </div>

                        {/* Pro Tier (Most Popular) */}
                        <div className="bg-slate-900 rounded-3xl shadow-2xl border border-slate-800 p-8 flex flex-col h-full relative transform md:-translate-y-6 z-10">
                            <div className="absolute top-0 right-8 transform -translate-y-1/2">
                                <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                                    En Popüler
                                </span>
                            </div>

                            <div className="mb-6">
                                <span className="inline-block px-3 py-1 bg-slate-800 text-blue-400 rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
                                    Pro
                                </span>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-extrabold text-white">$5.99</span>
                                </div>
                                <p className="text-slate-400 mt-2 text-sm">Profesyoneller ve yoğun kullanıcılar için.</p>
                            </div>

                            <div className="flex-grow">
                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-white font-medium">500.000 Karakter</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-slate-300">Daha hızlı işlem süresi</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-slate-300">Taranmış PDF'ler için OCR desteği</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-slate-300">Öncelikli müşteri desteği</span>
                                    </li>
                                </ul>
                            </div>

                            <a
                                href="#" // Placeholder for Shopify Link
                                className="w-full block text-center py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold transition-colors shadow-lg shadow-blue-500/30"
                            >
                                Satın Al
                            </a>
                        </div>

                        {/* Value Tier */}
                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 flex flex-col h-full transition-transform hover:-translate-y-2 duration-300">
                            <div className="mb-6">
                                <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
                                    Avantajlı
                                </span>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-extrabold text-slate-900">$9.99</span>
                                </div>
                                <p className="text-slate-500 mt-2 text-sm">En iyi fiyat/performans oranı.</p>
                            </div>

                            <div className="flex-grow">
                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700 font-medium">1.000.000 Karakter</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">En yüksek öncelikli işlem</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">Tam çeviri geçmişi</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">En iyi fiyat/performans</span>
                                    </li>
                                </ul>
                            </div>

                            <a
                                href="#" // Placeholder for Shopify Link
                                className="w-full block text-center py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-colors shadow-md"
                            >
                                Sınırları Zorla
                            </a>
                        </div>

                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white py-20 px-4 border-t border-gray-100">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Sıkça Sorulan Sorular</h2>
                        <p className="text-slate-600">Fiyatlandırma ve sistem işleyişi hakkında merak ettikleriniz.</p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-2xl overflow-hidden transition-all duration-200 hover:border-blue-200 bg-white"
                            >
                                <button
                                    className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
                                    onClick={() => toggleFaq(index)}
                                >
                                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-500 transition-transform duration-200 ${openFaq === index ? 'rotate-180 bg-blue-50 text-blue-500' : ''}`}>
                                        <ChevronDown className="w-5 h-5" />
                                    </div>
                                </button>

                                <div
                                    className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-48 pb-6 opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <p className="text-slate-600 leading-relaxed">
                                        {faq.answer}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 text-center bg-blue-50 rounded-2xl p-8 border border-blue-100">
                        <HelpCircle className="w-8 h-8 text-blue-500 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Başka sorularınız mı var?</h3>
                        <p className="text-slate-600 mb-6">Destek ekibimiz size yardımcı olmaktan mutluluk duyacaktır.</p>
                        <Link to="/iletisim" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                            Bize Ulaşın <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Pricing;
